/**
 * clinicalEvents.js
 * Domain constants and triage priority algorithms for clinical telemetry events.
 */

export const EVENT_TYPE_COLORS = {
	SOS: '#E8474C',
	FALLDOWN: '#E8474C',
	SHOCK: '#E8474C',
	GEO_FENCE: '#E8474C',
	OXYGEN: '#E8474C',
	TEMPERATURE: '#E8474C',
	BLOOD_PRESSURE: '#ffb84dff',
	POWER_OFF: '#ffb84dff',
	WATCH_REMOVED: '#ffb84dff',
	WATCH_DISCONNECTED: '#ffb84dff',
	LOW_BATTERY: '#ffb84dff',
	MEDICINE_REMINDER: '#4FD1C5',
	WATCH_CONNECTED: '#4FD1C5',
};

export const EVENT_TYPE_PRIORITY_ORDER = [
	'SOS',
	'FALLDOWN',
	'SHOCK',
	'GEO_FENCE',
	'OXYGEN',
	'TEMPERATURE',
	'BLOOD_PRESSURE',
	'POWER_OFF',
	'WATCH_REMOVED',
	'WATCH_DISCONNECTED',
	'LOW_BATTERY',
	'MEDICINE_REMINDER',
	'WATCH_CONNECTED',
];

/**
 * Returns the numeric priority index of a clinical event.
 * Lower index = higher urgency/priority.
 *
 * @param {string} eventType
 * @returns {number} 0 (highest) to 11 (lowest), 99 for unknown
 */
export const getEventPriority = eventType => {
	const idx = EVENT_TYPE_PRIORITY_ORDER.indexOf(eventType);
	return idx === -1 ? 99 : idx;
};

/**
 * Maps the highest-urgency clinical event to the exact tab and metric in the patient's profile.
 *
 * @param {Array} events - List of active clinical events for the patient.
 * @returns {{ tab: string, metric: string|null, topEvent: Object|null, isCritical: boolean }}
 */
export const getEventContextualTarget = (events = []) => {
	if (!events || events.length === 0) {
		return { tab: 'personal', metric: null, topEvent: null, isCritical: false };
	}

	const sorted = events.slice().sort((a, b) => {
		const typeA = a.type || a.name || '';
		const typeB = b.type || b.name || '';
		return getEventPriority(typeA) - getEventPriority(typeB);
	});

	const topEvent = sorted[0];
	const type = topEvent.type || topEvent.name || '';
	const priority = getEventPriority(type);
	const isCritical = priority <= 5 || ['SOS', 'FALLDOWN', 'SHOCK', 'GEO_FENCE', 'OXYGEN', 'TEMPERATURE'].includes(type);

	switch (type) {
		case 'GEO_FENCE':
			return { tab: 'geolocation', metric: null, topEvent, isCritical };
		case 'SHOCK':
			return { tab: 'historical', metric: 'heartRate', topEvent, isCritical };
		case 'OXYGEN':
			return { tab: 'historical', metric: 'oxygen', topEvent, isCritical };
		case 'TEMPERATURE':
			return { tab: 'historical', metric: 'temperature', topEvent, isCritical };
		case 'BLOOD_PRESSURE':
			return { tab: 'historical', metric: 'bloodPressure', topEvent, isCritical };
		case 'SOS':
		case 'FALLDOWN':
			return { tab: 'phonebook', metric: null, topEvent, isCritical };
		case 'LOW_BATTERY':
		case 'POWER_OFF':
		case 'WATCH_REMOVED':
		case 'WATCH_DISCONNECTED':
		case 'WATCH_CONNECTED':
			return { tab: 'smartwatch', metric: null, topEvent, isCritical };
		case 'MEDICINE_REMINDER':
			return { tab: 'medicine', metric: null, topEvent, isCritical };
		default:
			return { tab: 'personal', metric: null, topEvent, isCritical };
	}
};

/**
 * Calculates current age in years from an ISO birthdate string or Date object.
 * Returns null if birthdate is invalid or out of realistic range.
 */
export const calculateAge = (birthdate: any) => {
	if (!birthdate) return null;
	const b = new Date(birthdate);
	if (isNaN(b.getTime())) return null;
	const today = new Date();
	let age = today.getFullYear() - b.getFullYear();
	const m = today.getMonth() - b.getMonth();
	if (m < 0 || (m === 0 && today.getDate() < b.getDate())) {
		age--;
	}
	return age >= 0 && age < 130 ? age : null;
};
